import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
    Autocomplete as MuiAutocomplete,
    AutocompleteChangeReason,
    TextField,
    AutocompleteInputChangeReason,
} from '@mui/material'
import {
    AnyAutocompleteProp,
    AutocompleteOption,
} from './AnyAutocomplete.types'
import { TEXT_REQUIRED_VALIDATOR } from '../validators'
import { translate } from '../i18n'
import { AnyComponentRef } from './AnyComponent.types'

function AnyAutocomplete({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    fullWidth = true,
    name,
    initialValue,
    label,
    options,
    grouped,
    required,
    disabled,
    freeSolo,
    disableClearable,
    prefillWhenSingle = true,
    register,
    notifier,
}: AnyAutocompleteProp) {
    const [value, setValue] = useState<AutocompleteOption | null>(
        () => options.find((el) => el.value === initialValue) ?? null
    )
    const [rawValue, setRawValue] = useState<string>(initialValue ?? '')
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()
    const [cleared, setCleared] = useState(false)

    const ref = useRef<AnyComponentRef<string>>({
        valid,
        dirty,
        value: rawValue,
        setBlur: () => setBlured(true),
    })

    const onChange = (
        event: React.SyntheticEvent,
        value: AutocompleteOption | string | null,
        reason: AutocompleteChangeReason
    ) => {
        setDirty(true)
        switch (reason) {
            case 'selectOption':
                if (typeof value === 'string') {
                    setValue({
                        value: value,
                        label: value,
                    })
                } else {
                    setValue(value)
                }
                setCleared(false)
                break
            case 'clear':
                setBlured(true)
                setValue(null)
                setCleared(true)
                break
            case 'blur':
                setBlured(true)
                break
            case 'createOption':
                const asString = value as string
                setValue({
                    value: asString,
                    label: asString,
                })
                break
            default:
        }
    }

    const onInputChange = (
        event: React.SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason === 'input') {
            setValue({
                value: value,
                label: value,
            })
        }
    }

    const onNotify = useCallback(
        (value?: string) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        const rawValue = value?.value ?? ''
        const valid = !required || TEXT_REQUIRED_VALIDATOR.isValid(rawValue)
        const error = valid ? undefined : TEXT_REQUIRED_VALIDATOR.getMessage()

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = rawValue

        setValid(valid)
        setError(error)
        setRawValue(rawValue)

        onNotify(rawValue)
    }, [value, dirty, required, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    const freeSoloCallback = useCallback(
        (value: AutocompleteOption | null) => {
            if (value || !initialValue) return value

            const currentOption = options.find(
                (el) => el.value === initialValue
            )
            if (currentOption) {
                return value
            }

            return {
                value: initialValue,
                label: initialValue,
            }
        },
        [initialValue, options]
    )

    const selectCallback = useCallback(
        (value: AutocompleteOption | null) => {
            if (initialValue) {
                const currentValue = options.find(
                    (el) => el.value === value?.value
                )
                if (currentValue && currentValue.value === initialValue) {
                    return value
                }
            }

            if (cleared) {
                return null
            }

            let initial = options.find((el) => el.value === initialValue)
            if (initial) {
                return initial
            }

            if (options.length === 1 && prefillWhenSingle) {
                return options[0]
            }

            return null
        },
        [options, initialValue, prefillWhenSingle, cleared]
    )

    useEffect(() => {
        if (freeSolo) {
            setValue(freeSoloCallback)
        } else {
            setValue(selectCallback)
        }
    }, [freeSolo, freeSoloCallback, selectCallback])

    const getOptionLabel = useCallback(
        (option?: string | AutocompleteOption) => {
            if (typeof option === 'string') {
                return option || '---'
            }
            return option?.label || '---'
        },
        []
    )

    return (
        <MuiAutocomplete
            value={value}
            options={options}
            groupBy={grouped ? (option) => option.group ?? '' : undefined}
            getOptionLabel={getOptionLabel}
            fullWidth={fullWidth}
            onChange={onChange}
            onInputChange={freeSolo ? onInputChange : undefined}
            clearText={translate('buttons.clear') as string}
            closeText={translate('buttons.close') as string}
            openText={translate('buttons.open') as string}
            noOptionsText={translate('no_values') as string}
            disabled={disabled}
            freeSolo={freeSolo}
            disableClearable={disableClearable}
            includeInputInList={freeSolo}
            openOnFocus={!freeSolo}
            isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
            }
            renderOption={(props, option) => (
                <li {...props} key={option.value}>
                    {option.label}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    variant={variant}
                    size={size}
                    margin={margin}
                    error={blured && !valid}
                    helperText={blured ? error : ''}
                />
            )}
        />
    )
}

export default memo(AnyAutocomplete)
